import {
  SurveyPage,
  QuestionType,
  ComposedSurveyPlugin,
  BaseComposedElement,
} from '@hyper-inferno/survey-elements-core';
import { PluginRuntimeHost } from './plugin-utils';

export class ComposedLogicPageBuilder {
  private rawPage: SurveyPage;
  private logicElements: BaseComposedElement[] = [];
  private pagePlugins: ComposedSurveyPlugin[] = [];
  private context: any; // Replace with your actual Context type

  constructor(rawPage: SurveyPage, context: any) {
    this.rawPage = rawPage;
    this.context = context;
  }

  /**
   * Registers the skip logic elements and automatically sorts them by rank ascending
   */
  public setLogicElements(elements: BaseComposedElement[]): this {
    this.logicElements = (elements ?? [])
      .filter((el) => el.type === QuestionType.SKIP_LOGIC)
      .sort((a, b) => a.rank - b.rank);
    return this;
  }

  /**
   * Sets page level plugins accessible during evaluation
   */
  public setPagePlugins(plugins: ComposedSurveyPlugin[]): this {
    this.pagePlugins = plugins ?? [];
    return this;
  }

  /**
   * Evaluates the rules sequentially and computes the target landing destination.
   * This encapsulates the core evaluation and chronological fallback strategy.
   */
  /**
   * Iterates through skip logic elements, evaluates their collection of elementPlugins
   * sequentially inside individual sandboxes, and resolves branching routes.
   */
  public async evaluateTarget(): Promise<{
    pageId: number;
    reason: 'skip_logic' | 'forward' | 'end' | 'plugin_jump';
  }> {
    const currentResponses = this.context.session.jsn_responses ?? {};
    // const handler = this.context.handlers[QuestionType.SKIP_LOGIC];

    // 1. Loop through each sorted logic element
    for (const el of this.logicElements) {
      // Target the array of plugins associated with this specific element
      const targetPlugins = el.elementPlugins ?? [];

      for (const pluginInstance of targetPlugins) {
        // Skip if the plugin instance doesn't have active execution hooks configured
        if (
          !pluginInstance.hooks ||
          Object.keys(pluginInstance.hooks).length === 0
        ) {
          continue;
        }

        // const pluginDef: ComposedSurveyPlugin = {
        //   usageId: pluginInstance.id ?? el.id, // Fallback to element ID if instance lacks one
        //   hooks: pluginInstance.hooks,
        //   instanceValues: pluginInstance.instanceValues || {},
        //   answerObject: currentResponses[el.id] || null, // Tied to the host element's answer namespace
        //   context: { backend_validated: 'true' },
        //   html: pluginInstance.html || '',
        // };

        const pluginHost = new PluginRuntimeHost({
          pluginDef: pluginInstance,
          sessionId: this.context.session.id,
          surveyId: this.context.bundle.survey_id,
          initialRegistry: currentResponses,
          currentPageId: this.rawPage.id,
          containerElement: null,
          onStateChange: (updatedState: { data: any }) => {
            if (updatedState.data) {
              currentResponses[el.id] = updatedState.data;
              // Sync the answer array/object mutations seamlessly to the session
              this.context.session.patchResponses({
                [el.id]: updatedState.data,
              });
            }
          },
          onMarkCompleted: (usageId: any) => {
            console.log(`Plugin instance [${usageId}] processed successfully.`);
          },
        });

        await pluginHost.initialize();
        const isPluginValid = await pluginHost.executeBeforeSubmit();

        if (!isPluginValid) {
          throw new Error(
            `Validation Error on plugin instance [${pluginInstance.id}]: ${pluginHost.getValidationError()}`,
          );
        }

        // Intercept execution sandbox state metrics for an early routing breakout
        const hostState = pluginHost.getPluginData();

        if (hostState?.forceJump) {
          await pluginHost.destroy();
          return { pageId: hostState.forceJump, reason: 'plugin_jump' };
        }

        await pluginHost.destroy();
      }

      // 2. If no plugin forced a breakout jump, execute standard Skip Logic Handler for the element
      const handler = this.context.handlers[el.type]
      if (handler) {
        const nextId = await handler.getNextPageId(
          el,
          this.context,
          this.context.handlers, // Pass the entire handlers map for context
        );

        if (nextId) {
          return { pageId: nextId, reason: 'skip_logic' };
        }
      }
    }

    // 3. FALLBACK: Normal chronological progression paths
    const nextChronologicalPage = this.context.bundle.fetchNextPageQuery(
      this.rawPage.id,
    );
    if (nextChronologicalPage) {
      return { pageId: nextChronologicalPage.id, reason: 'forward' };
    }

    // 4. ULTIMATE FALLBACK: Ending route node
    const endingPageId = this.context.bundle.getFirstEnding().id;
    return { pageId: endingPageId, reason: 'end' };
  }

  // const handler = this.context.handlers[QuestionType.SKIP_LOGIC];

  // // 1. Run evaluations through the registered skip logic handlers
  // if (handler) {
  //   for (const el of this.logicElements) {
  //     const nextId = await handler.getNextPageId(
  //       el,
  //       this.context,
  //       this.pagePlugins, // Available to the evaluation context
  //     );

  //     if (nextId) {
  //       return { pageId: nextId, reason: 'skip_logic' };
  //     }
  //   }
  // }

  // // 2. Fallback: Check chronological next page sequential path
  // const nextChronologicalPage = this.context.bundle.fetchNextPageQuery(
  //   this.rawPage.id,
  // );
  // if (nextChronologicalPage) {
  //   return { pageId: nextChronologicalPage.id, reason: 'forward' };
  // }

  // // 3. Absolute Fallback: Send to the ending route
  // const endingPageId = this.context.bundle.getFirstEnding().id;
  // return { pageId: endingPageId, reason: 'end' };
}

// getComposedElements(rawPage: SurveyPage) {
//   const elementPromises = (rawPage.elements ?? []).map(async (el) => {
//     const handler = this.context.handlers[el.type];
//     // Get current language from session
//     const lang = this.context.session.survey_language;

//     // Select the translation using the helperIO
//     const question_text = el.translations?.['question_text']?.[lang]
//       ? pickTextFromCodex(el.translations, 'question_text', lang)
//       : el.question_text;
//     // 1. Build the base element properties
//     const elementBuilder = new ComposedElementBuilder({
//       name: el.name,
//       type: el.type,
//       question_text: new UniversalPiping().transform(
//         question_text,
//         this.context,
//       ),
//       id: el.id,
//       is_skippable: !el.required,
//       element_type: ElementType.QUESTION_ELEMENT,
//       page_id: rawPage.id,
//       rank: el.rank,
//       required: el.required ?? true,
//     });

//     // 2. Use Handler to build the complex config
//     if (handler) {
//       const config = await handler.buildConfig(el, this.context);
//       elementBuilder.setQuestionConfig(config);
//     }
//     elementBuilder.setAnswerObject(
//       this.context.session.jsn_responses[String(el.id)] ?? {},
//     );

//     const conditonalDisplay = this.context.bundle.getConditionSetsByElementId(
//       String(el.id),
//     );

//     if (conditonalDisplay) {
//       const composedConditionSet = this.buildComposedLogic(
//         conditonalDisplay,
//         this.context,
//       );
//       elementBuilder.addConditionalDisplayMeta(composedConditionSet);
//     }

//     const elementPlugins = this.context.bundle.getPluginsByElementId(el.id);
//     elementBuilder.setElementPlugins(elementPlugins);

//     return elementBuilder.build();
//   });

//   return Promise.all(elementPromises);
// }
